import * as fs from 'node:fs';
import { randomUUID } from 'node:crypto';
import * as path from 'node:path';

const MIN_SNAPSHOT_DISK_CHARGE_BYTES = 4096;

export class ByteBudget {
  readonly limitBytes: number;
  private reservedBytes = 0;

  constructor(limitBytes: number) {
    assertByteCount(limitBytes, 'byte budget limit');
    if (limitBytes === 0) {
      throw new Error('byte budget limit must be positive');
    }
    this.limitBytes = limitBytes;
  }

  get usedBytes(): number {
    return this.reservedBytes;
  }

  get remainingBytes(): number {
    return this.limitBytes - this.reservedBytes;
  }

  tryReserve(bytes: number): boolean {
    assertByteCount(bytes, 'byte reservation');
    if (bytes > this.remainingBytes) {
      return false;
    }
    this.reservedBytes += bytes;
    return true;
  }

  reserveAvailable(bytes: number): number {
    assertByteCount(bytes, 'byte reservation');
    const reserved = Math.min(bytes, this.remainingBytes);
    this.reservedBytes += reserved;
    return reserved;
  }
}

export function writeBudgetedUtf8File({
  file,
  content,
  budget,
  description,
}: {
  file: string;
  content: string;
  budget: ByteBudget;
  description: string;
}): void {
  const bytes = Buffer.byteLength(content, 'utf8');
  if (!budget.tryReserve(bytes)) {
    throw new Error(
      `${description} exceeds the aggregate review metadata budget `
      + `(${bytes} bytes requested; ${budget.remainingBytes} of ${budget.limitBytes} bytes remaining).`,
    );
  }
  replaceFileAtomically(file, Buffer.from(content, 'utf8'));
}

export function snapshotBudgetCharge(payloadBytes: number): number {
  assertByteCount(payloadBytes, 'snapshot payload size');
  return Math.max(payloadBytes, MIN_SNAPSHOT_DISK_CHARGE_BYTES);
}

export function readBoundedRegularFile({
  file,
  maxBytes,
  description,
  limitName,
}: {
  file: string;
  maxBytes: number;
  description: string;
  limitName: string;
}): Buffer {
  assertByteCount(maxBytes, `${limitName} limit`);
  const descriptor = fs.openSync(
    file,
    fs.constants.O_RDONLY | fs.constants.O_NOFOLLOW | fs.constants.O_NONBLOCK,
  );
  try {
    const entry = fs.fstatSync(descriptor);
    if (!entry.isFile()) {
      throw new Error(`${description} is not a regular file: ${file}`);
    }
    if (entry.size > maxBytes) {
      throw new Error(`${description} exceeds ${limitName} (${maxBytes}): ${entry.size} bytes.`);
    }
    const chunks: Buffer[] = [];
    let total = 0;
    while (true) {
      if (total === maxBytes) {
        const sentinel = Buffer.allocUnsafe(1);
        if (fs.readSync(descriptor, sentinel, 0, 1, null) > 0) {
          throw new Error(`${description} exceeds ${limitName} (${maxBytes}): more than ${maxBytes} bytes.`);
        }
        break;
      }
      const chunk = Buffer.allocUnsafe(Math.min(64 * 1024, maxBytes - total));
      const bytesRead = fs.readSync(descriptor, chunk, 0, chunk.length, null);
      if (bytesRead === 0) {
        break;
      }
      total += bytesRead;
      chunks.push(chunk.subarray(0, bytesRead));
    }
    return Buffer.concat(chunks, total);
  } finally {
    fs.closeSync(descriptor);
  }
}

export function replaceFileAtomically(file: string, content: Buffer): void {
  fs.mkdirSync(path.dirname(file), { recursive: true });
  const temporary = path.join(
    path.dirname(file),
    `.${path.basename(file)}.codex-review-${randomUUID()}.tmp`,
  );
  try {
    removeFileEntryIfPresent(temporary);
    fs.writeFileSync(temporary, content, { mode: 0o600, flag: 'wx' });
    fs.renameSync(temporary, file);
  } finally {
    fs.rmSync(temporary, { force: true });
  }
}

function removeFileEntryIfPresent(file: string): void {
  try {
    fs.unlinkSync(file);
  } catch (error) {
    if ((error as NodeJS.ErrnoException).code !== 'ENOENT') {
      throw error;
    }
  }
}

function assertByteCount(value: number, description: string): void {
  if (!Number.isSafeInteger(value) || value < 0) {
    throw new Error(`${description} must be a non-negative safe integer`);
  }
}
